import {
    Avatar,
    CalendarHeader,
    OfficeButton,
    TableComponent,
} from "components";
import React, { Fragment, useState } from "react";
import {
    EnvelopeIcon,
    PhoneIcon,
    IdentificationIcon,
    ClockIcon,
} from "@heroicons/react/24/outline";

import UpdatePasswordContainer from "./update-password";
import moment from "moment";
import { useCurrentUser } from "_graphql/cache/auth";
import { useEmployeeActivities } from "hooks/data/use-employees";
import { usePagination } from "hooks";

const ProfilePage: React.FC = () => {
    const currentUser = useCurrentUser();
    const [showUpdatePassword, setShowUpdatePassword] = useState(false);
    const { offset, limit, toDate, fromDate } = usePagination();
    const { activities, loading, refetch } = useEmployeeActivities({
        filter: {
            date_from: moment(fromDate).format("YYYY-MM-DD HH:mm:ss"),
            date_to: moment(toDate).endOf("D").format("YYYY-MM-DD HH:mm:ss"),
        },
        pagination: {
            limit: limit,
            offset: offset,
        },
    });
    return (
        <Fragment>
            <main className="flex-1 flex flex-col overflow-hidden bg-background-500">
                <CalendarHeader
                    renderActions={() => (
                        <OfficeButton
                            onClick={() => setShowUpdatePassword(true)}
                            size="sm"
                            variant="primary">
                            Update Password
                        </OfficeButton>
                    )}
                />
                <div className="flex-1 p-2 sm:p-4 md:p-6 overflow-y-auto bg-background-500">
                    {/* Profile Header Section */}
                    <div className="relative isolate overflow-hidden rounded-lg bg-gradient-to-br from-primary-400 to-primary-600 px-6 py-8 shadow-lg mb-6">
                        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primary-500/50 to-primary-700/50" />
                        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                            <Avatar
                                size="lg"
                                alt={
                                    currentUser?.employee?.emp_abbrv
                                        ?.split("")
                                        .join(" ") || "N A"
                                }
                            />
                            <div className="flex-1 text-center sm:text-left">
                                <h1 className="text-2xl sm:text-3xl font-bold text-white">
                                    {currentUser?.employee?.employee_first_name}{" "}
                                    {currentUser?.employee?.employee_last_name}
                                </h1>
                                <div className="mt-2 inline-flex items-center bg-white/20 backdrop-blur-sm px-4 py-1.5 rounded-full">
                                    <IdentificationIcon className="h-4 w-4 text-white mr-2" />
                                    <span className="text-sm font-medium text-white">
                                        {currentUser?.broker_role?.position || "N/A"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact Information Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div className="bg-card-500 rounded-lg shadow p-6 flex items-start gap-4">
                            <div className="flex-shrink-0">
                                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                                    <EnvelopeIcon className="h-6 w-6 text-blue-600" />
                                </div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-500">
                                    Email Address
                                </p>
                                <p className="mt-1 text-sm text-primary font-medium truncate">
                                    {currentUser?.employee?.employee_email}
                                </p>
                            </div>
                        </div>

                        <div className="bg-card-500 rounded-lg shadow p-6 flex items-start gap-4">
                            <div className="flex-shrink-0">
                                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                                    <PhoneIcon className="h-6 w-6 text-green-600" />
                                </div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-500">
                                    Phone Number
                                </p>
                                <p className="mt-1 text-sm text-primary font-medium">
                                    {
                                        currentUser?.employee
                                            ?.employee_phonenumber
                                    }
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Activity Table */}
                    <div className="bg-card-500 rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <div className="flex items-center gap-2">
                                <ClockIcon className="h-5 w-5 text-gray-400" />
                                <h2 className="text-lg font-semibold text-primary">
                                    Recent Activity
                                </h2>
                            </div>
                        </div>
                        <div className="p-6">
                            <TableComponent
                                hasSearch
                                title={"Login Activities"}
                                columns={[
                                    {
                                        name: "Device IP",
                                        accessor: "device_ip",
                                    },
                                    { name: "Device", accessor: "device_type" },
                                    { name: "Country", accessor: "country" },
                                    { name: "City", accessor: "city" },
                                    {
                                        name: "Logged in on",
                                        accessor: "created_at",
                                    },
                                ]}
                                data={activities}
                                loading={loading}
                                refetch={refetch}
                            />
                        </div>
                    </div>
                </div>
            </main>
            <UpdatePasswordContainer
                open={showUpdatePassword}
                setOpen={setShowUpdatePassword}
            />
        </Fragment>
    );
};

export default ProfilePage;
